import { sectionRenderer } from '@theme/section-renderer';

const REWARD_TITLE = 'Free 5 mL Decant on $100+';
const REWARD_PROPERTY = '_ArkX reward';
let catalogPromise;

function money(cents) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function shippingProgress(subtotal, threshold) {
  const remaining = Math.max(0, threshold - subtotal);
  if (remaining === 0) {
    return '<div class="arkx-shipping-progress"><span>Shipping reward</span><strong>You unlocked free ground shipping.</strong></div>';
  }

  const percent = Math.min(100, Math.round((subtotal / threshold) * 100));
  return `<div class="arkx-shipping-progress"><span>Free ground shipping</span><strong>${money(remaining)} away</strong><div class="arkx-reward-track" aria-label="${percent}% toward free shipping"><i style="width:${percent}%"></i></div></div>`;
}

async function getCatalog(url) {
  catalogPromise ??= fetch(url).then((response) => {
    if (!response.ok) throw new Error('Gift choices are temporarily unavailable.');
    return response.json();
  });
  return catalogPromise;
}

async function refreshCartSections() {
  const ids = new Set(
    [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
  );

  await Promise.all([...ids].map((id) => sectionRenderer.renderSection(id, {
    cache: false,
    mode: id === 'cart-drawer-section' ? 'hydration' : 'full',
    injectStylesheet: id === 'cart-drawer-section',
  })));
}

class ArkXCartReward extends HTMLElement {
  connectedCallback() {
    this.render().catch(() => {
      this.innerHTML = '<p class="arkx-reward-note">Gift choices are temporarily unavailable. Your other cart items are unaffected.</p>';
    });
  }

  async render() {
    const threshold = Number(this.dataset.threshold || 10000);
    const shippingThreshold = Number(this.dataset.shippingThreshold || 5000);
    const [cart, catalog] = await Promise.all([
      fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json()),
      getCatalog(this.dataset.catalogUrl),
    ]);
    const isRewardItem = (item) => item.properties?.[REWARD_PROPERTY]
      || item.discounts?.some((discount) => discount.title === REWARD_TITLE);
    const rewardItem = cart.items.find(isRewardItem);
    const qualifyingSubtotal = cart.items.reduce((total, item) => (
      isRewardItem(item) ? total : total + item.original_line_price
    ), 0);
    const amountRemaining = Math.max(0, threshold - qualifyingSubtotal);
    const shippingMarkup = shippingProgress(qualifyingSubtotal, shippingThreshold);

    if (rewardItem) {
      const isDiscounted = rewardItem.discounts?.some((discount) => discount.title === REWARD_TITLE)
        || rewardItem.final_line_price === 0;
      this.innerHTML = `${shippingMarkup}<div class="arkx-reward-earned"><span>ArkX order reward</span><strong>${isDiscounted ? 'Your free 5 mL is in the cart.' : 'Keep your merchandise total at $100 to keep this gift free.'}</strong><small>${rewardItem.product_title} · 5 mL</small></div>`;
      return;
    }

    if (amountRemaining > 0) {
      const percent = Math.min(100, Math.round((qualifyingSubtotal / threshold) * 100));
      this.innerHTML = `${shippingMarkup}<div class="arkx-reward-progress"><div><span>Order reward</span><strong>${money(amountRemaining)} away from a free 5 mL</strong></div><div class="arkx-reward-track" aria-label="${percent}% toward free 5 mL"><i style="width:${percent}%"></i></div><small>Choose any eligible 5 mL valued at $9.99 or less after reaching $100.</small></div>`;
      return;
    }

    const choices = catalog
      .filter((item) => item.prices?.['5'] <= 9.99 && item.variants?.['5']?.available)
      .sort((a, b) => a.rank - b.rank);
    this.innerHTML = `${shippingMarkup}<div class="arkx-reward-unlocked"><span>Reward unlocked</span><strong>Choose your free 5 mL decant</strong><label><span class="visually-hidden">Free 5 mL fragrance</span><select data-arkx-gift-select>${choices.map((item) => `<option value="${item.variants['5'].id}">${item.brand} · ${item.name} (${money(item.prices['5'] * 100)})</option>`).join('')}</select></label><button type="button" data-arkx-add-gift>Add my free 5 mL</button><small>One per order. The discount appears automatically in your cart.</small><p role="status" aria-live="polite"></p></div>`;
    this.querySelector('[data-arkx-add-gift]')?.addEventListener('click', () => this.addGift());
  }

  async addGift() {
    const button = this.querySelector('[data-arkx-add-gift]');
    const select = this.querySelector('[data-arkx-gift-select]');
    const status = this.querySelector('[role="status"]');
    if (!button || !select) return;

    button.disabled = true;
    button.textContent = 'Adding your gift…';
    status.textContent = '';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id: Number(select.value),
            quantity: 1,
            properties: { [REWARD_PROPERTY]: 'Free 5 mL on $100+' },
          }],
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'The gift could not be added.');

      await refreshCartSections();
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((item) => item.json());
      document.querySelectorAll('[data-arkx-cart-count]').forEach((element) => {
        element.textContent = `(${cart.item_count})`;
      });
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Add my free 5 mL';
      status.textContent = error.message || 'Please try again.';
    }
  }
}

if (!customElements.get('arkx-cart-reward')) {
  customElements.define('arkx-cart-reward', ArkXCartReward);
}
